<script>
/**
 * Assignment modal for one parameter's incoming-CC mapping (MDL-267).
 * Opened from CcBadge. Rendered as a native <dialog> (top layer) so it
 * always centres on screen and is never clipped by a pedal at the bottom
 * of the list or covered by the mapping overlay.
 *
 * Continuous/int params get lo/hi output-range fields (defaulting to the
 * param's full range; lo > hi inverts the response). Enum params get the
 * CC field only. Mutates effect.mappings in place — the parent deep-watch
 * persists via the normal save/UPDATE_EFFECT flow.
 *
 * A CC may drive several params — the firmware fully supports multi-param
 * mappings (spec §3), so assigning a CC already used elsewhere is allowed.
 * When it is, the modal shows a non-blocking warning naming every other
 * target it also controls; only a reserved or out-of-range CC blocks Set.
 */
import { toRaw } from 'vue';
import {
  RESERVED_CCS,
  PARAM_LABELS,
  paramSpec,
  isRanged,
  getMapping,
  setMapping,
  clearMapping,
  defaultRange,
  findCcOwners,
  enumValueRanges,
} from '../js/ccMapping.js';

// "A" · "A and B" · "A, B and C"
function formatList(items) {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// The param's own bounds — range inputs clamp to these.
function boundsFor(effect, param) {
  const spec = paramSpec(effect, param);
  if (!spec) return [null, null];
  return defaultRange(spec, effect.clock_sync === true);
}

export default {
  props: {
    effect: {
      type: Object,
      required: true
    },
    param: {
      type: String,
      required: true
    },
    // All effects in the patch, for the in-use check. The effect prop must
    // be an element of this array (identity match excludes self).
    effects: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  // Device-side MIDI Learn controller (MDL-315), provided by the editor shell
  // when the connected device advertises the cc_mapping.learn capability.
  // Absent (null) → no Learn button, manual CC entry only.
  inject: {
    midiLearn: { default: null },
  },

  emits: ['close'],

  data() {
    const mapping = getMapping(this.effect, this.param);
    const [dlo, dhi] = boundsFor(this.effect, this.param);
    return {
      cc: mapping?.cc ?? null,
      lo: mapping?.lo ?? dlo,
      hi: mapping?.hi ?? dhi,
      error: null,
      listening: false,   // true while armed and waiting for a control to move
    };
  },

  computed: {
    label() {
      return PARAM_LABELS[this.param] ?? this.param;
    },

    assigned() {
      return getMapping(this.effect, this.param) !== null;
    },

    ranged() {
      return isRanged(this.effect, this.param);
    },

    bounds() {
      return boundsFor(this.effect, this.param);
    },

    isRate() {
      return paramSpec(this.effect, this.param)?.kind === 'rate' && !this.effect.clock_sync;
    },

    // Switch target (mute) — CC field only, plus an on/off explainer.
    isSwitch() {
      return paramSpec(this.effect, this.param)?.kind === 'switch';
    },

    switchHint() {
      return this.param === 'mute'
        ? 'Incoming 0 = silenced, 1 or more = active.'
        : 'Incoming 0 = off, 1 or more = on.';
    },

    // For enum-like params: which incoming CC values select which option
    // (exact bucket-rule maths, shared with firmware). Null for continuous.
    enumRanges() {
      return enumValueRanges(this.effect, this.param);
    },

    step() {
      return this.isRate ? 0.01 : 1;
    },

    // Non-blocking notice: the entered CC already drives other params in the
    // patch. Lists every other target so the user knows this control will move
    // them together. Null when the CC is unused, empty, reserved, or invalid.
    ccWarning() {
      const cc = Number(this.cc);
      if (!Number.isInteger(cc) || cc < 0 || cc > 127 || RESERVED_CCS.has(cc)) {
        return null;
      }
      // Identity via toRaw: the effect prop and the injected patch array may
      // wrap the same underlying object in different reactive proxies.
      const rawEffect = toRaw(this.effect);
      const owners = findCcOwners(this.effects, cc, {
        skipEffectIndex: this.effects.findIndex((e) => toRaw(e) === rawEffect),
        skipParam: this.param,
      });
      if (owners.length === 0) return null;
      return `CC ${cc} also controls ${formatList(owners.map((o) => o.label))}.`;
    }
  },

  mounted() {
    this.$refs.dialog.showModal();
    // Focus (and select) the CC field so it's ready to type into.
    this.$nextTick(() => {
      const input = this.$refs.ccInput;
      if (input) { input.focus(); input.select(); }
    });
  },

  beforeUnmount() {
    // Never leave the device armed if we're torn down mid-learn (overlay
    // toggled off, patch changed, etc.).
    if (this.listening) this.midiLearn?.cancel();
    // Defensive: if the parent removes us while still open, leave the top
    // layer cleanly.
    if (this.$refs.dialog?.open) this.$refs.dialog.close();
  },

  methods: {
    close() {
      if (this.listening) this.midiLearn?.cancel();
      // Fires the dialog's native 'close' event → @close emits to the parent.
      this.$refs.dialog?.close();
    },

    clampToBounds(value, fallback) {
      const n = Number(value);
      if (Number.isNaN(n)) return fallback;
      const [bLo, bHi] = this.bounds;
      return Math.max(bLo, Math.min(bHi, n));
    },

    // Blocking validation shared by manual entry and a learned capture.
    // Returns an error string, or null if the CC may be assigned. A CC already
    // mapped elsewhere is NOT an error — it's surfaced as a warning (see
    // ccWarning) since multi-param mappings are supported.
    ccError(cc) {
      if (!Number.isInteger(cc) || cc < 0 || cc > 127) {
        return 'Enter a CC number between 0 and 127';
      }
      if (RESERVED_CCS.has(cc)) {
        return `CC ${cc} is reserved and can't be mapped`;
      }
      return null;
    },

    assign() {
      const cc = Number(this.cc);
      const err = this.ccError(cc);
      if (err) {
        this.error = err;
        return;
      }

      const entry = { cc };
      if (this.ranged) {
        const [dlo, dhi] = this.bounds;
        entry.lo = this.clampToBounds(this.lo, dlo);
        entry.hi = this.clampToBounds(this.hi, dhi);
      }
      setMapping(this.effect, this.param, entry);
      this.error = null;
      this.close();
    },

    clear() {
      clearMapping(this.effect, this.param);
      this.close();
    },

    // MIDI Learn: arm the device, then fill the CC field from the control the
    // user operates. The range stays a manual choice — learn only captures the
    // CC number — so we do NOT auto-save; the user sets/keeps the range and
    // clicks Set. A captured CC in use elsewhere surfaces the same non-blocking
    // warning as typing it.
    async learn() {
      if (!this.midiLearn || this.listening) return;
      this.listening = true;
      this.error = null;
      try {
        const result = await this.midiLearn.start();
        if (result && result.reason === 'captured' && typeof result.cc === 'number') {
          this.cc = result.cc;
          this.error = this.ccError(result.cc);
        }
        // 'cancelled' / 'timeout' → just leave the modal as it was.
      } catch {
        this.error = 'MIDI Learn failed — is the device connected?';
      } finally {
        this.listening = false;
      }
    },

    stopLearn() {
      this.midiLearn?.cancel();
      // start()'s promise resolves 'cancelled' → the finally clears listening.
    }
  }
}
</script>
<template>
  <dialog ref="dialog" class="modal cc-assign-modal" @close="$emit('close')">
    <div class="modal-box max-w-xs">
      <h3 class="font-bold text-base mb-4">{{ label }} — Incoming CC</h3>

      <div class="cc-modal-fields">
      <label class="cc-popover__field">
        <span>CC</span>
        <input
          ref="ccInput"
          v-model.number="cc"
          type="number"
          min="0"
          max="127"
          step="1"
          class="input input-sm input-bordered cc-popover__input"
          placeholder="0–127"
          :disabled="listening"
          @keyup.enter="assign"
        />
        <button
          v-if="midiLearn"
          type="button"
          class="btn btn-sm btn-ghost cc-popover__learn"
          :disabled="listening"
          @click="learn"
        >Learn</button>
      </label>

      <p v-if="listening" class="cc-popover__listening">
        <span class="loading loading-spinner loading-xs"></span>
        Listening — move a control…
        <button type="button" class="btn btn-xs btn-ghost" @click="stopLearn">Stop</button>
      </p>

      <template v-if="ranged">
        <p class="cc-popover__hint">Map incoming CC messages to the following range</p>
        <div class="cc-popover__range">
          <label class="cc-popover__field">
            <span>Lo</span>
            <input
              v-model.number="lo"
              type="number"
              :min="Math.min(bounds[0], bounds[1])"
              :max="Math.max(bounds[0], bounds[1])"
              :step="step"
              class="input input-sm input-bordered cc-popover__input"
              @keyup.enter="assign"
            />
          </label>
          <label class="cc-popover__field">
            <span>Hi</span>
            <input
              v-model.number="hi"
              type="number"
              :min="Math.min(bounds[0], bounds[1])"
              :max="Math.max(bounds[0], bounds[1])"
              :step="step"
              class="input input-sm input-bordered cc-popover__input"
              @keyup.enter="assign"
            />
          </label>
        </div>
        <p class="cc-popover__hint">
          Incoming CC of 0 maps to Lo.<br />
          Incoming CC of 127 maps to Hi.<br />
          Set Lo greater than Hi to invert the behaviour.
        </p>
      </template>

      <template v-if="enumRanges">
        <div class="cc-popover__enum-map">
          <div v-for="row in enumRanges" :key="row.option" class="cc-popover__enum-row">
            <span class="cc-popover__enum-range">{{ row.from === row.to ? row.from : `${row.from}–${row.to}` }}</span>
            <span class="cc-popover__enum-label">{{ row.label }}</span>
          </div>
        </div>
      </template>

      <p v-if="isSwitch" class="cc-popover__hint">{{ switchHint }}</p>

      <p v-if="ccWarning" class="cc-popover__warning">{{ ccWarning }}</p>
      <p v-if="error" class="cc-popover__error">{{ error }}</p>
      </div>

      <div class="modal-action mt-5">
        <button v-if="assigned" type="button" class="btn btn-sm btn-ghost mr-auto cc-popover__clear" @click="clear">Clear</button>
        <button type="button" class="btn btn-sm btn-ghost" @click="close">Cancel</button>
        <button type="button" class="btn btn-sm btn-primary" @click="assign">Set</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button>close</button>
    </form>
  </dialog>
</template>
